import { createEntityAdapter } from '@reduxjs/toolkit';
import type { Draft } from 'immer';

import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';

import { billScoutRecordedTransactionsApi } from './billScoutRecordedTransactionsApi';
import { toBillScoutRecordedTransactionsRequestError } from './billScoutRecordedTransactionsErrors';
import {
  type AddBillScoutRecordedTransactionsPayload,
  BILL_SCOUT_RECORDED_TRANSACTIONS_REDUCER_KEY,
  type BillScoutRecordedTransaction,
  type BillScoutRecordedTransactionsModelState,
  type BillScoutRecordedTransactionsRequestError,
  type BillScoutRecordedTransactionsState,
  type DeleteBillScoutRecordedTransactionPayload,
  type FetchBillScoutRecordedTransactionsPayload,
} from './billScoutRecordedTransactionsTypes';

export const billScoutRecordedTransactionsAdapter = createEntityAdapter<BillScoutRecordedTransaction, string>({
  selectId: (association) => association.id,
});

const createModelState = (): BillScoutRecordedTransactionsModelState => ({
  associationIds: [],
  dataRevision: 0,
  hasLoaded: false,
  fetch: {
    status: 'idle',
    error: null,
    requestId: null,
    startedDataRevision: null,
  },
  mutation: {
    status: 'idle',
    error: null,
    pendingRequestIds: [],
  },
  needsReconciliation: false,
});

const initialState: BillScoutRecordedTransactionsState = billScoutRecordedTransactionsAdapter.getInitialState({
  models: {},
});

type DraftState = Draft<BillScoutRecordedTransactionsState>;
type DraftModelState = Draft<BillScoutRecordedTransactionsModelState>;

const getOrCreateModelState = (state: DraftState, stModelId: string): DraftModelState => {
  const current = state.models[stModelId];
  if (current) return current;

  const model = createModelState();
  state.models[stModelId] = model;
  return state.models[stModelId];
};

const uniqueIds = (ids: ReadonlyArray<string>): string[] => [...new Set(ids)];

const removeUnreferencedAssociations = (state: DraftState, candidateIds: ReadonlyArray<string>): void => {
  const referencedIds = new Set(Object.values(state.models).flatMap((model) => model.associationIds));
  billScoutRecordedTransactionsAdapter.removeMany(
    state,
    candidateIds.filter((id) => !referencedIds.has(id)),
  );
};

const replaceModelAssociations = (
  state: DraftState,
  stModelId: string,
  associations: ReadonlyArray<BillScoutRecordedTransaction>,
): void => {
  const model = state.models[stModelId];
  if (!model) return;

  const previousIds = model.associationIds;
  const nextIds = uniqueIds(associations.map((association) => association.id));
  billScoutRecordedTransactionsAdapter.setMany(state, associations);
  model.associationIds = nextIds;
  removeUnreferencedAssociations(
    state,
    previousIds.filter((id) => !nextIds.includes(id)),
  );
};

const mergeModelAssociations = (
  state: DraftState,
  stModelId: string,
  associations: ReadonlyArray<BillScoutRecordedTransaction>,
): void => {
  const model = state.models[stModelId];
  if (!model) return;

  billScoutRecordedTransactionsAdapter.setMany(state, associations);
  model.associationIds = uniqueIds([...model.associationIds, ...associations.map((association) => association.id)]);
  model.dataRevision += 1;
};

const removeAssociation = (state: DraftState, stModelId: string, recordedTransactionId: string): void => {
  let targetRevisionIncremented = false;
  for (const [modelId, model] of Object.entries(state.models)) {
    if (model.associationIds.includes(recordedTransactionId)) {
      model.associationIds = model.associationIds.filter((id) => id !== recordedTransactionId);
      model.dataRevision += 1;
      targetRevisionIncremented ||= modelId === stModelId;
    }
  }
  if (!targetRevisionIncremented) {
    const targetModel = state.models[stModelId];
    if (targetModel) {
      targetModel.dataRevision += 1;
    }
  }
  billScoutRecordedTransactionsAdapter.removeOne(state, recordedTransactionId);
};

const isPendingMutation = (model: DraftModelState, requestId: string): boolean =>
  model.mutation.pendingRequestIds.includes(requestId);

const beginMutation = (model: DraftModelState, requestId: string): void => {
  if (model.mutation.pendingRequestIds.length === 0) {
    model.mutation.error = null;
  }
  // A write can reach the server even if its response later fails. Invalidate
  // any GET that started before this attempt so stale data cannot look canonical.
  model.dataRevision += 1;
  model.mutation.pendingRequestIds.push(requestId);
  model.mutation.status = 'pending';
};

const rejectMutation = (model: DraftModelState, error: BillScoutRecordedTransactionsRequestError): void => {
  model.mutation.error = error;
  if (error.ambiguous) {
    // A concurrent GET may have started after this write was sent but before
    // its outcome became ambiguous. It cannot safely reconcile server state.
    model.dataRevision += 1;
    model.needsReconciliation = true;
  }
};

const settleMutation = (model: DraftModelState, requestId: string): void => {
  model.mutation.pendingRequestIds = model.mutation.pendingRequestIds.filter((id) => id !== requestId);
  if (model.mutation.pendingRequestIds.length > 0) {
    model.mutation.status = 'pending';
  } else {
    model.mutation.status = model.mutation.error ? 'failed' : 'succeeded';
  }
};

const getRejectedError = (
  payload: BillScoutRecordedTransactionsRequestError | undefined,
  error: unknown,
): BillScoutRecordedTransactionsRequestError => payload ?? toBillScoutRecordedTransactionsRequestError(error);

export const billScoutRecordedTransactionsSlice = createAppSlice({
  name: BILL_SCOUT_RECORDED_TRANSACTIONS_REDUCER_KEY,
  initialState,
  reducers: (create) => ({
    fetchRecordedTransactions: create.asyncThunk.withTypes<{
      rejectValue: BillScoutRecordedTransactionsRequestError;
    }>()(
      async ({ stModelId }: FetchBillScoutRecordedTransactionsPayload, thunkApi) => {
        try {
          const associations = await billScoutRecordedTransactionsApi.fetch(stModelId);
          return { associations, stModelId };
        } catch (error) {
          return thunkApi.rejectWithValue(toBillScoutRecordedTransactionsRequestError(error));
        }
      },
      {
        pending(state, action) {
          const model = getOrCreateModelState(state, action.meta.arg.stModelId);
          model.fetch.status = 'pending';
          model.fetch.error = null;
          model.fetch.requestId = action.meta.requestId;
          model.fetch.startedDataRevision = model.dataRevision;
        },
        fulfilled(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || model.fetch.requestId !== action.meta.requestId) return;

          if (model.fetch.startedDataRevision !== model.dataRevision) {
            model.fetch.status = model.hasLoaded ? 'succeeded' : 'idle';
            model.needsReconciliation = true;
            return;
          }

          replaceModelAssociations(state, action.payload.stModelId, action.payload.associations);
          model.fetch.status = 'succeeded';
          model.fetch.error = null;
          model.hasLoaded = true;
          model.needsReconciliation = false;
        },
        rejected(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || model.fetch.requestId !== action.meta.requestId) return;

          if (model.fetch.startedDataRevision !== model.dataRevision) {
            model.fetch.status = model.hasLoaded ? 'succeeded' : 'idle';
            model.needsReconciliation = true;
            return;
          }

          model.fetch.status = 'failed';
          model.fetch.error = getRejectedError(action.payload, action.error);
        },
        settled(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || model.fetch.requestId !== action.meta.requestId) return;
          model.fetch.requestId = null;
          model.fetch.startedDataRevision = null;
        },
      },
    ),

    addRecordedTransactions: create.asyncThunk.withTypes<{
      rejectValue: BillScoutRecordedTransactionsRequestError;
    }>()(
      async (payload: AddBillScoutRecordedTransactionsPayload, thunkApi) => {
        try {
          const associations = await billScoutRecordedTransactionsApi.add(payload);
          return { associations, stModelId: payload.stModelId };
        } catch (error) {
          return thunkApi.rejectWithValue(toBillScoutRecordedTransactionsRequestError(error));
        }
      },
      {
        pending(state, action) {
          beginMutation(getOrCreateModelState(state, action.meta.arg.stModelId), action.meta.requestId);
        },
        fulfilled(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || !isPendingMutation(model, action.meta.requestId)) return;
          mergeModelAssociations(state, action.payload.stModelId, action.payload.associations);
        },
        rejected(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || !isPendingMutation(model, action.meta.requestId)) return;
          rejectMutation(model, getRejectedError(action.payload, action.error));
        },
        settled(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || !isPendingMutation(model, action.meta.requestId)) return;
          settleMutation(model, action.meta.requestId);
        },
      },
    ),

    deleteRecordedTransaction: create.asyncThunk.withTypes<{
      rejectValue: BillScoutRecordedTransactionsRequestError;
    }>()(
      async (payload: DeleteBillScoutRecordedTransactionPayload, thunkApi) => {
        try {
          await billScoutRecordedTransactionsApi.delete(payload.recordedTransactionId);
          return payload;
        } catch (error) {
          return thunkApi.rejectWithValue(toBillScoutRecordedTransactionsRequestError(error));
        }
      },
      {
        pending(state, action) {
          beginMutation(getOrCreateModelState(state, action.meta.arg.stModelId), action.meta.requestId);
        },
        fulfilled(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || !isPendingMutation(model, action.meta.requestId)) return;
          removeAssociation(state, action.payload.stModelId, action.payload.recordedTransactionId);
        },
        rejected(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || !isPendingMutation(model, action.meta.requestId)) return;
          rejectMutation(model, getRejectedError(action.payload, action.error));
        },
        settled(state, action) {
          const model = state.models[action.meta.arg.stModelId];
          if (!model || !isPendingMutation(model, action.meta.requestId)) return;
          settleMutation(model, action.meta.requestId);
        },
      },
    ),
  }),
});

export const billScoutRecordedTransactionsActions = billScoutRecordedTransactionsSlice.actions;
